import * as React from 'react';
import { LucideIcon } from 'lucide-react';
import { t } from '~i18n';
import ActionBar, { BarAction } from '~components/ActionBar/ActionBar';

export interface ActionProps {
  icon: string;
  lucide?: LucideIcon;
  label: string;
}

// Cap the chip list so a large selection doesn't overflow the bar; the overflow
// count rides in a trailing chip.
const MAX_CHIPS = 7;
const BULK_PROHIBITED_ACTIONS = ['residence-map', 'edit'];

interface Props {
  count: number;
  /** Indices of the selected rows (into the currently-rendered `rows`). */
  selectedIndices: number[];
  actions: ActionProps[];
  onActionClick?: (rowIndices: number[], action: string) => void;
  isActionVisible?: (rowIndex: number, action: string) => boolean;
  getRowLabel?: (rowIndex: number) => React.ReactNode;
  onClear: () => void;
}

/**
 * Bulk-action bar for a selected set of table rows: chips of a per-row property
 * on the left, the row actions as icon buttons on the right. A thin adapter
 * from the table's action model onto the generic `ActionBar`.
 */
export const SelectionActionBar: React.FC<Props> = ({
  count,
  selectedIndices,
  actions,
  onActionClick,
  isActionVisible,
  getRowLabel,
  onClear,
}) => {
  const chips: React.ReactNode[] = getRowLabel
    ? selectedIndices.length <= MAX_CHIPS
      ? selectedIndices.map((i) => getRowLabel(i))
      : [...selectedIndices.slice(0, MAX_CHIPS).map((i) => getRowLabel(i)), `+${selectedIndices.length - MAX_CHIPS}`]
    : [];

  // Show an action only when it's applicable to every selected row.
  const barActions: BarAction[] = actions
    .filter((a) => !BULK_PROHIBITED_ACTIONS.includes(a.icon))
    .filter((a) => selectedIndices.every((i) => isActionVisible?.(i, a.icon) !== false))
    .sort((a, b) => (a.icon === 'delete' ? -1 : b.icon === 'delete' ? 1 : 0))
    .map((a) => ({
      id: a.icon,
      label: a.label,
      icon: a.icon,
      lucide: a.lucide,
      variant: a.icon == 'delete' ? 'danger' : 'primary',
      onClick: () => onActionClick?.(selectedIndices, a.icon),
    }));

  return (
    <ActionBar
      title={`${count} ${t('genericTable.selected')}`}
      chips={chips}
      actions={barActions}
      onClose={onClear}
      closeLabel={t('genericTable.clearSelectionAria')}
    />
  );
};

export default SelectionActionBar;
